import type { CreateWorkflowConditionPayload } from '../wf.js';
import type {
  AllocationTargetDimension,
  AllocationTargetsState,
  CreditProfile,
  WithholdingProfile,
} from '../wf-fact-values.js';

/**
 * ALC-4565 (W2-3, D35 to D37, D49 to D51): the approval-rule offer on the bill page.
 *
 * Right after a person's own approval makes a bill APPROVED, the post-approval dialog offers ONE
 * editable rule for the next bills of the vendor, with two parts the person ticks (D53): "Enviar
 * para aprovação" (a `bill_updated` workflow that submits) and "Aprovar" (a
 * `BILL_APPROVAL_WORKFLOW_INIT` workflow that auto-approves within its last changer's authority).
 * Either part can be saved alone, or both together.
 *
 * The API returns structured data only (ids, codes, decimal text); every sentence is i18n on the
 * web. Conditions carry ids; names are resolved by the API for display only.
 */

/** How far above the source bill's amount the suggested ceiling goes: 10 %, rounded down. */
export const APPROVAL_OFFER_CEILING_PERCENT = '10';

/**
 * The split mode of one allocation row (D50): `all` "Rateio entre estes" (same set, any %), `exact`
 * "Exatamente estes %" (same set, same stored %), `any` "Pelo menos um destes". `all_within` (P55)
 * is never offered.
 */
export type ApprovalOfferSplitMode = 'all' | 'exact' | 'any';

/** The engine operator of each split mode (ALC-4658). */
export const APPROVAL_OFFER_MODE_OPERATOR = {
  all: 'same_targets',
  exact: 'exact_split',
  any: 'any_within',
} as const satisfies Record<ApprovalOfferSplitMode, string>;

/** One target of an allocation row: its id, display data, and the stored percent of a simple split. */
export interface ApprovalOfferTarget {
  id: string;
  /** Display only (resolved by the API): "CC-OPS", "3.1.02", "P-101". */
  code: string | null;
  name: string | null;
  /**
   * The source bill's stored percent as two-decimal text ("60.00") when the bill is a bill-level
   * simple split that offers "Exatamente estes %"; otherwise `null` (P50, P51, P66).
   */
  percent: string | null;
}

/** The allocation row dimension: a flat dimension, or a custom segment (with its id). */
export type ApprovalOfferAllocationDimension = AllocationTargetDimension | 'segment';

interface ApprovalOfferRowBase {
  /** Stable row key: `glAccounts`, `costCenters`, ..., `segment:<segmentId>`, `paymentMethod`, ... */
  key: string;
  /** Whether the row is a condition of the rule. An unticked row is not a condition at all. */
  ticked: boolean;
  /** The row cannot be ticked (the withholding row when the source has no trusted profile). */
  disabled: boolean;
}

export interface ApprovalOfferAllocationRow extends ApprovalOfferRowBase {
  kind: 'allocation';
  dimension: ApprovalOfferAllocationDimension;
  /** Set for a custom segment row. */
  segmentId: string | null;
  /** The segment's own name ("Projeto"); `null` for a flat dimension (the web labels it). */
  segmentName: string | null;
  targets: ApprovalOfferTarget[];
  mode: ApprovalOfferSplitMode;
  /** Whether "Exatamente estes %" can be offered for this row (a non-null exact split value). */
  exactAvailable: boolean;
  /**
   * P16: the source bill had a cost center (department), so the rule also requires one
   * (`cost_center is_not_null`). Shown as "(preenchido)" inside the row. Flat CC / department only.
   */
  requireFilled: boolean;
  /** P57: a custom segment whose value varied over the vendor's last approved bills. */
  variesRecently: boolean;
}

export interface ApprovalOfferPaymentMethodRow extends ApprovalOfferRowBase {
  kind: 'payment_method';
  /** One value = `equals`; two or more = `in`. */
  values: string[];
}

export interface ApprovalOfferWithholdingRow extends ApprovalOfferRowBase {
  kind: 'withholding';
  /** The source bill's own facts (ALC-4563). `null` = no trusted profile: the row is disabled. */
  withholdingProfile: WithholdingProfile | null;
  creditProfile: CreditProfile | null;
}

export interface ApprovalOfferClientReimbursableRow extends ApprovalOfferRowBase {
  kind: 'client_reimbursable';
  value: boolean;
}

export interface ApprovalOfferAmountRow extends ApprovalOfferRowBase {
  kind: 'amount';
  /** "Total até": decimal text with two places. */
  ceiling: string;
  /** The amount the ceiling came from (net payment amount, else total). */
  sourceAmount: string;
}

export type ApprovalOfferRow =
  | ApprovalOfferAllocationRow
  | ApprovalOfferPaymentMethodRow
  | ApprovalOfferWithholdingRow
  | ApprovalOfferClientReimbursableRow
  | ApprovalOfferAmountRow;

/** Notes about the prefill (codes; the web words them). */
export const ApprovalOfferNoteCode = {
  NO_GL_ACCOUNT_ON_SOURCE: 'NO_GL_ACCOUNT_ON_SOURCE',
  NO_COST_CENTER_ON_SOURCE: 'NO_COST_CENTER_ON_SOURCE',
  NO_DEPARTMENT_ON_SOURCE: 'NO_DEPARTMENT_ON_SOURCE',
  NO_MANAGERIAL_ACCOUNT_ON_SOURCE: 'NO_MANAGERIAL_ACCOUNT_ON_SOURCE',
  NO_PAYMENT_METHOD_ON_SOURCE: 'NO_PAYMENT_METHOD_ON_SOURCE',
  SOURCE_PROFILE_UNAVAILABLE: 'SOURCE_PROFILE_UNAVAILABLE',
} as const;
export type ApprovalOfferNoteCode =
  (typeof ApprovalOfferNoteCode)[keyof typeof ApprovalOfferNoteCode];

/**
 * Why "Aprovar" is not offered, in the order they are checked (the first failing
 * one is shown).
 */
export const ApprovalOfferBlockedReasonCode = {
  NO_AUTHORITY: 'NO_AUTHORITY',
  APPROVED_BY_RULE: 'APPROVED_BY_RULE',
  MULTI_APPROVER_CHAIN: 'MULTI_APPROVER_CHAIN',
  CURRENCY_NOT_BRL: 'CURRENCY_NOT_BRL',
  PROFILE_UNAVAILABLE: 'PROFILE_UNAVAILABLE',
  NO_CEILING: 'NO_CEILING',
  CEILING_ABOVE_AUTHORITY: 'CEILING_ABOVE_AUTHORITY',
  NO_COST_CENTER: 'NO_COST_CENTER',
  ANY_MODE_COST_CENTER: 'ANY_MODE_COST_CENTER',
  COST_CENTER_NOT_COVERED: 'COST_CENTER_NOT_COVERED',
  DEPARTMENT_NOT_COVERED: 'DEPARTMENT_NOT_COVERED',
  VENDOR_NOT_COVERED: 'VENDOR_NOT_COVERED',
} as const;
export type ApprovalOfferBlockedReasonCode =
  (typeof ApprovalOfferBlockedReasonCode)[keyof typeof ApprovalOfferBlockedReasonCode];

/** The check order of {@link ApprovalOfferBlockedReasonCode}. */
export const APPROVAL_OFFER_BLOCKED_REASON_ORDER: readonly ApprovalOfferBlockedReasonCode[] = [
  ApprovalOfferBlockedReasonCode.NO_AUTHORITY,
  ApprovalOfferBlockedReasonCode.APPROVED_BY_RULE,
  ApprovalOfferBlockedReasonCode.MULTI_APPROVER_CHAIN,
  ApprovalOfferBlockedReasonCode.CURRENCY_NOT_BRL,
  ApprovalOfferBlockedReasonCode.PROFILE_UNAVAILABLE,
  ApprovalOfferBlockedReasonCode.NO_CEILING,
  ApprovalOfferBlockedReasonCode.CEILING_ABOVE_AUTHORITY,
  ApprovalOfferBlockedReasonCode.NO_COST_CENTER,
  ApprovalOfferBlockedReasonCode.ANY_MODE_COST_CENTER,
  ApprovalOfferBlockedReasonCode.COST_CENTER_NOT_COVERED,
  ApprovalOfferBlockedReasonCode.DEPARTMENT_NOT_COVERED,
  ApprovalOfferBlockedReasonCode.VENDOR_NOT_COVERED,
];

/**
 * The first reason "Aprovar" is not offered, with the data its sentence needs:
 * money as decimal text, ids the web resolves to names through {@link BillApprovalOffer.labels}.
 */
export interface ApprovalOfferBlockedReason {
  code: ApprovalOfferBlockedReasonCode;
  /** CEILING_ABOVE_AUTHORITY: the rule's ceiling. */
  ceiling?: string;
  /** NO_CEILING / CEILING_ABOVE_AUTHORITY: the authority limit the sentence names. */
  authorityMax?: string;
  /** COST_CENTER_NOT_COVERED: the rule's cost centers the authority does not cover. ANY_MODE: the
   * authority's own cost centers. */
  costCenterIds?: string[];
  /** DEPARTMENT_NOT_COVERED: the departments not covered. */
  departmentIds?: string[];
}

/** One ACTIVE approval authority of the viewer; empty lists mean "no restriction". */
export interface ApprovalOfferAuthority {
  authorityId: string;
  /** Decimal text with two places. */
  maxApprovalAmount: string;
  costCenterIds: string[];
  departmentIds: string[];
  vendorIds: string[];
}

/** P60: the chain the source bill went through. */
export interface ApprovalOfferSourceChain {
  /** One step, pattern SINGLE or ANY_OF. */
  oneApproval: boolean;
  /** The viewer actioned that approval. */
  viewerGaveIt: boolean;
}

export interface ApprovalOfferPrefill {
  entityId: string;
  /** The conditions of the rule as the system prefilled it, in the shape `createWorkflow` takes. */
  conditions: CreateWorkflowConditionPayload[];
  ceiling: { amount: string; sourceAmount: string; percent: string };
  origin: { kind: 'APPROVAL_OFFER'; sourceBillId: string };
}

/**
 * The source bill's own facts the dialog re-checks the edited rows against (the amber note "A NF 90
 * não passaria nesta regra"). Built by the API from the same fact loader as the prefill.
 */
export interface ApprovalOfferSourceSnapshot {
  allocations: AllocationTargetsState;
  paymentMethod: string | null;
  /** The amount fact (net payment amount, else total), decimal text. The rule's amount condition. */
  amount: string | null;
  /**
   * The amount the bill's classification splits are taken from (total, else subtotal; a
   * reimbursement's approved or requested amount), decimal text with 2 places. The rateio table's
   * "Na NF" shares use it, so they equal the bill's own classification amounts.
   */
  classificationBaseAmount: string | null;
  /**
   * What each target took on the bill's own SIMPLE split, as stored on its classification row:
   * decimal text with 2 places, keyed by target id (segments: by segment id, then value id). The
   * "Na NF" column shows these, so R$ 100,01 at 50/50 keeps R$ 50,00 on Operações and R$ 50,01 on
   * TI whatever their ids sort as. `null` when the bill has no such split or a target has no
   * stored amount (the column then cuts the shares from `classificationBaseAmount`).
   */
  allocationAmounts: ApprovalOfferAllocationAmounts | null;
}

/** The stored amount of each target of a SIMPLE split, by dimension and target id. */
export type ApprovalOfferAllocationAmounts = Record<
  AllocationTargetDimension,
  Record<string, string>
> & { segments: Record<string, Record<string, string>> };

export interface BillApprovalOffer {
  offerKind: 'approval_automation';
  billId: string;
  /** "NF 90" source: the vendor bill number or NF number; `null` when the bill has none. */
  billNumber: string | null;
  entityId: string;
  entityName: string;
  vendorId: string;
  vendorName: string;
  vendorShortName: string;
  prefill: ApprovalOfferPrefill;
  rows: ApprovalOfferRow[];
  notes: ApprovalOfferNoteCode[];
  /**
   * `false` when the vendor already has an ACTIVE send rule (`sendRule`): its bills reach approval
   * alone, so the offer makes the approve part only ("Aprovar" adds the approve rule).
   */
  canSendToApproval: boolean;
  /** The vendor's ACTIVE send rule (`bill_updated` + `submit_for_approval`), when it has one. */
  sendRule: { workflowId: string; name: string } | null;
  canApproveAutomatically: boolean;
  approveBlockedReason: ApprovalOfferBlockedReason | null;
  /** Case (ii) of D49: the viewer's own submit was approved by a rule (P60: never auto). */
  approvedByRule: boolean;
  /** `isApprovalOfferAllowedForEntity(...).autoApprove`: the entity's functional currency is BRL. */
  autoApproveCurrencyAllowed: boolean;
  viewerAuthority: ApprovalOfferAuthority[];
  sourceChain: ApprovalOfferSourceChain;
  /** Display labels by id for the authority's cost centers / departments / vendors. */
  labels: Record<string, string>;
  /** The source bill's facts, for the amber note. */
  source: ApprovalOfferSourceSnapshot;
}

export interface BillApprovalOfferResponse {
  offer: BillApprovalOffer | null;
}

/** Body of `POST /ap/bills/:billId/approval-offer/approve` (D35). */
export interface SaveApprovalOfferRulesPayload {
  conditions: CreateWorkflowConditionPayload[];
  sendName: string;
  approveName: string;
  description?: string | null;
  /**
   * `false` saves the approve rule alone ("Aprovar" without "Enviar para aprovação"): a person still
   * sends the bills, and the rule approves them. Default `true`. An ACTIVE send rule of the vendor
   * is never created twice either way.
   */
  withSendRule?: boolean;
}

export interface SaveApprovalOfferRulesResponse {
  /** The created or existing send rule; `null` when the approve rule was saved alone. */
  sendWorkflowId: string | null;
  approveWorkflowId: string;
}

/**
 * Body of `POST /ap/bills/:billId/approval-offer/send` ("Enviar para aprovação", ALC-4565
 * fix r1 F5): the send rule alone, under the same D35 lock and offer re-check as the approve save.
 */
export interface SaveApprovalOfferSendRulePayload {
  conditions: CreateWorkflowConditionPayload[];
  sendName: string;
  description?: string | null;
}

export interface SaveApprovalOfferSendRuleResponse {
  sendWorkflowId: string;
}

// ---------------------------------------------------------------------------
// Error codes (D35, D36): stable codes with pt-BR + en copy.
// ---------------------------------------------------------------------------

export const ApprovalOfferErrorCode = {
  /** dismiss / approve on a bill that has no offer (no vendor, not approved, ...). */
  APPROVAL_OFFER_NOT_AVAILABLE: 'APPROVAL_OFFER_NOT_AVAILABLE',
  /** 400 (fix r1 F4): the saved conditions do not name the bill's vendor (one root-level
   * `vendor equals <bill vendor>`) or join conditions with "or". The entity is always the bill's. */
  APPROVAL_OFFER_CONDITIONS_MISMATCH: 'APPROVAL_OFFER_CONDITIONS_MISMATCH',
  /** 409 (fix r1 F4): the offer itself refuses "Aprovar" for this bill;
   * `details.reasonCode` is the {@link ApprovalOfferBlockedReasonCode}. */
  APPROVAL_OFFER_APPROVE_BLOCKED: 'APPROVAL_OFFER_APPROVE_BLOCKED',
} as const;
export type ApprovalOfferErrorCode =
  (typeof ApprovalOfferErrorCode)[keyof typeof ApprovalOfferErrorCode];

/** D36 save-time refusals of an ACTIVE auto-approve workflow. */
export const AutoApproveErrorCode = {
  AUTO_APPROVE_AUTHORITY_INSUFFICIENT: 'AUTO_APPROVE_AUTHORITY_INSUFFICIENT',
  AUTO_APPROVE_REQUIRES_PERSON_SAVE: 'AUTO_APPROVE_REQUIRES_PERSON_SAVE',
  AUTO_APPROVE_TRIGGER_UNSUPPORTED: 'AUTO_APPROVE_TRIGGER_UNSUPPORTED',
  AUTO_APPROVE_SCOPE_UNPROVABLE: 'AUTO_APPROVE_SCOPE_UNPROVABLE',
  AUTO_APPROVE_CEILING_REQUIRED: 'AUTO_APPROVE_CEILING_REQUIRED',
  AUTO_APPROVE_ENTITY_REQUIRED: 'AUTO_APPROVE_ENTITY_REQUIRED',
  AUTO_APPROVE_CURRENCY_NOT_BRL: 'AUTO_APPROVE_CURRENCY_NOT_BRL',
} as const;
export type AutoApproveErrorCode = (typeof AutoApproveErrorCode)[keyof typeof AutoApproveErrorCode];

/** pt-BR + en copy of the codes above (generic forms; the API message names amounts and targets). */
export const APPROVAL_OFFER_ERROR_COPY: Record<
  ApprovalOfferErrorCode | AutoApproveErrorCode,
  { en: string; 'pt-BR': string }
> = {
  APPROVAL_OFFER_NOT_AVAILABLE: {
    en: 'There is no rule offer for this bill.',
    'pt-BR': 'Não há oferta de regra para esta conta.',
  },
  APPROVAL_OFFER_CONDITIONS_MISMATCH: {
    en: 'The rule must name this bill\'s vendor and join its conditions with "and" only.',
    'pt-BR': 'A regra precisa indicar o fornecedor desta conta e juntar as condições só com "e".',
  },
  APPROVAL_OFFER_APPROVE_BLOCKED: {
    en: 'Automatic approval is not available for this bill. Save the rule that sends it for approval instead.',
    'pt-BR':
      'A aprovação automática não está disponível para esta conta. Salve a regra que só envia para aprovação.',
  },
  AUTO_APPROVE_AUTHORITY_INSUFFICIENT: {
    en: 'Your approval authority does not cover this automatic approval rule.',
    'pt-BR': 'A sua alçada de aprovação não cobre esta regra de aprovação automática.',
  },
  AUTO_APPROVE_REQUIRES_PERSON_SAVE: {
    en: 'An active automatic approval rule can only be saved by a person on the screen, not by an integration or agent.',
    'pt-BR':
      'Uma regra de aprovação automática ativa só pode ser salva por uma pessoa na tela, não por uma integração ou agente.',
  },
  AUTO_APPROVE_TRIGGER_UNSUPPORTED: {
    en: 'Automatic approval only runs in approval rules, not in bill update rules.',
    'pt-BR':
      'A aprovação automática só funciona em regras de aprovação, não em regras de atualização de contas.',
  },
  AUTO_APPROVE_SCOPE_UNPROVABLE: {
    en: 'An automatic approval rule must join its conditions with "and" only, so its reach can be checked against your authority.',
    'pt-BR':
      'Uma regra de aprovação automática precisa juntar as condições só com "e", para que o alcance dela possa ser conferido com a sua alçada.',
  },
  AUTO_APPROVE_CEILING_REQUIRED: {
    en: 'An automatic approval rule needs a maximum amount ("Total até").',
    'pt-BR': 'Uma regra de aprovação automática precisa de um valor máximo ("Total até").',
  },
  AUTO_APPROVE_ENTITY_REQUIRED: {
    en: 'An automatic approval rule must belong to one entity.',
    'pt-BR': 'Uma regra de aprovação automática precisa pertencer a uma entidade.',
  },
  AUTO_APPROVE_CURRENCY_NOT_BRL: {
    en: 'Automatic approval only works for entities in Brazilian reais.',
    'pt-BR': 'A aprovação automática só vale para entidades em reais.',
  },
};
